import React, { useCallback, useState } from "react";
import {
  AlignHorizontalCenter,
  ArrowForward,
  CheckCircle,
  Edit,
  Error,
  Layers,
  PictureInPictureOutlined,
  PowerInput,
  Settings,
  Timeline,
  Warning,
} from "@mui/icons-material";
import { Alert, AlertTitle, alpha, Box, Button, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { Progress } from "ui";

import { useGetAccountModulesQuery, useGetVersionQuery, useUpdateVersionArchitectureMutation } from "fond/api";
import { DEFAULT_DATA_PREP_CONFIGURATION } from "fond/architecture/dataPrepConfiguration";
import { summarizeDataPrepConfiguration } from "fond/architecture/designPreferences/cableOptions";
import { useFeatureFlag } from "fond/featureFlags";
import { useCanEditPlannerInputs } from "fond/hooks/useCanEditPlannerInputs";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { extendedInputLayerGroupList, extendedNetworkSourceLayerGroupList, inputLayerGroups, LayerGroup } from "fond/layers";
import OverlaysSettingModal from "fond/map/Overlays/OverlaysSettingModal";
import { dataPrepStageConfig } from "fond/project/panels/dataPrepStages";
import { usePlannerDataPrepStatus } from "fond/project/panels/ExtendedDesign/hooks/usePlannerDataPrepStatus";
import InputDataPanel from "fond/project/panels/InputDataPanel";
import PanelGroup from "fond/project/panels/InputPanel/PanelGroup";
import { DataPrepGenerateOptions, toDataPrepRunParameters } from "fond/project/panels/RunDataPrep/dataPrepRunParameters";
import { DataPrepLayerGroup } from "fond/project/panels/RunDataPrep/dataPrepStatus";
import RunDataPrep from "fond/project/panels/RunDataPrep/RunDataPrep";
import { loadProject, openArchitectureModal } from "fond/project/redux";
import { Project, WORKFLOW_KEYS } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { accountModuleCheck, Actions } from "fond/utils/permissions";
import { StackedNavigationButton, useAccordionStepperContext, useStackedNavigationContext } from "fond/widgets";

const networkRequiredMessage = "An underground or aerial network is required.";

// Navigation buttons for the candidate network layer groups; each empty message sentence is shown on its own line
const layerGroupButtons: Array<{
  title: string;
  icon: React.ReactElement;
  group: LayerGroup;
  statusGroup: Exclude<DataPrepLayerGroup, "inputs">;
  navigationKey: string;
  emptyMessage: string[];
}> = [
  {
    title: "Underground path and points",
    icon: <PowerInput />,
    group: inputLayerGroups.inUnderground,
    statusGroup: "underground",
    navigationKey: "extended-design-underground",
    emptyMessage: [networkRequiredMessage, "An underground network can be generated using the data preparation tools below."],
  },
  {
    title: "Aerial span and poles",
    icon: <AlignHorizontalCenter />,
    group: inputLayerGroups.spanPole,
    statusGroup: "aerial",
    navigationKey: "extended-design-aerial",
    emptyMessage: [networkRequiredMessage],
  },
  {
    title: "Leadins",
    icon: <Timeline />,
    group: inputLayerGroups.inLeadIns,
    statusGroup: "leadins",
    navigationKey: "extended-design-leadins",
    emptyMessage: ["Leadins are required and can be generated using the data preparation tools below."],
  },
];

interface DataPrepPanelProps {
  project: Project;
  versionId: string;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ project, versionId, readOnly }: DataPrepPanelProps) => {
  const dispatch = useAppDispatch();
  const { open } = useStackedNavigationContext();
  const { open: openPane } = useAccordionStepperContext();
  const { data: version } = useGetVersionQuery(versionId ?? "", { skip: !versionId });
  const [updateVersionArchitecture, { isLoading: isSavingArchitecture }] = useUpdateVersionArchitectureMutation();
  const { enqueueSnackbar } = useSnackbar();
  const dataPrepStatus = usePlannerDataPrepStatus(versionId);
  const { groups } = dataPrepStatus;
  const { value: canReviewProblematicParcels } = useFeatureFlag("street_parcel_identification");
  const [generate, setGenerate] = useState<DataPrepGenerateOptions>({ underground: true, leadins: true });
  const [showOverlays, setShowOverlays] = useState(false);
  const isLocked = !useCanEditPlannerInputs(readOnly);
  const { data: accountModules } = useGetAccountModulesQuery(project.Account.ID);
  const hasOverlaysModule = accountModuleCheck(accountModules, Actions.CUSTOM_OVERLAYS);
  const canEditOverlays = usePermissionCheck(Actions.CUSTOM_OVERLAYS, project.Permission.Level);

  const hasNetworkLayer = groups.underground.isPresent || groups.aerial.isPresent;

  const handleOnComplete = useCallback(async () => {
    // TODO: we should probably go to the 'Design' step automagically once this finishes?
    await dispatch(loadProject({ uuid: project.ID, forceRefetch: true }));
  }, [dispatch, project.ID]);

  const { workflowState, stages, runWorkflow, cancelWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.PLANNER_EXTENDED.DATA_PREP,
    onComplete: handleOnComplete,
  });

  const isDataPrepping = workflowState === "STARTING" || workflowState === "IN_PROGRESS";
  const isComplete = dataPrepStatus.hasRequiredInputLayers && dataPrepStatus.isDataPrepComplete;
  const canDataPrep = Boolean(runWorkflow) && Boolean(version?.Architecture) && !isDataPrepping && !isSavingArchitecture;
  const designPreferencesSummary = version?.Architecture
    ? summarizeDataPrepConfiguration(version.Architecture.DataPrepConfiguration ?? DEFAULT_DATA_PREP_CONFIGURATION)
    : undefined;

  const handleOpenSettings = () => {
    dispatch(openArchitectureModal("DesignPreferences"));
  };

  const handleDataPrep = async () => {
    if (!runWorkflow) {
      return;
    }

    // Architectures saved outside planner_extended may lack a data prep configuration, so ensure we set one.
    if (version?.Architecture && !version.Architecture.DataPrepConfiguration) {
      try {
        await updateVersionArchitecture({ versionId, architecture: { DataPrepConfiguration: DEFAULT_DATA_PREP_CONFIGURATION } }).unwrap();
      } catch {
        enqueueSnackbar("Failed to save Data Prep Settings.", { variant: "error" });
        return;
      }
    }
    runWorkflow(toDataPrepRunParameters(generate));
  };

  const handleCancelDataPrep = () => {
    cancelWorkflow?.();
  };

  const handleModifyData = () => {
    open("extended-design-modifications");
  };

  const handleIdentifyStreetParcels = () => {
    open("identify-street-parcels");
  };

  const handleOpenOverlays = () => {
    setShowOverlays(true);
  };

  const renderLayerGroupStatus = (statusGroup: Exclude<DataPrepLayerGroup, "inputs">, emptyMessage: string[]) => {
    const { isPresent, isComplete: isGroupComplete, missingLayerNames } = groups[statusGroup];
    if (isGroupComplete) {
      return <CheckCircle sx={{ color: (theme) => theme.palette.success.main, fontSize: 18 }} />;
    }
    if (isPresent) {
      return (
        <Tooltip title={`Missing ${missingLayerNames.join(", ").toLowerCase()}`}>
          <Warning sx={{ color: (theme) => theme.palette.warning.main, fontSize: 18 }} />
        </Tooltip>
      );
    }
    // An empty network group only needs attention while the version has no network at all
    if (statusGroup === "leadins" || !hasNetworkLayer) {
      return (
        <Tooltip
          title={emptyMessage.map((sentence, index) => (
            <Box key={sentence} mt={index > 0 ? 1 : 0}>
              {sentence}
            </Box>
          ))}
        >
          <Error color="action" sx={{ fontSize: 18 }} aria-label={emptyMessage.join(" ")} />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <>
      {isComplete && !isDataPrepping && (
        <Alert
          severity="success"
          variant="compact"
          sx={{ mb: 1, "& .MuiAlert-action": { alignItems: "center", pt: 0 } }}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              endIcon={<ArrowForward />}
              sx={{ borderColor: (theme) => alpha(theme.palette.success.main, 0.4), "& .MuiButton-endIcon": { ml: 0.5 } }}
              onClick={() => openPane("design")}
            >
              Continue
            </Button>
          }
        >
          Your design is ready to be generated.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation failed</AlertTitle>
          Data could not be generated.
        </Alert>
      )}

      {!isDataPrepping && (
        <>
          <PanelGroup title="Inputs">
            <InputDataPanel readOnly={readOnly} inputs={extendedInputLayerGroupList} dense />
          </PanelGroup>

          <PanelGroup title="Candidate network">
            {layerGroupButtons.map(({ title, icon, group, statusGroup, navigationKey, emptyMessage }) => (
              <StackedNavigationButton
                key={group.id}
                data-testid={`data-prep-layer-navigation-${group.id}`}
                icon={icon}
                title={title}
                onClick={() => open(navigationKey)}
                checked={groups[statusGroup].isComplete}
                iconSize="small"
                disabled={isLocked}
                rightElement={renderLayerGroupStatus(statusGroup, emptyMessage)}
                variant="outlined"
                sx={{ mt: 0, mb: 0.5, "&:last-child": { mb: 0 } }}
              />
            ))}

            <Typography variant="body3" fontWeight="500" display="block" mt={1} mb={0.5}>
              Data preparation
            </Typography>

            {canReviewProblematicParcels && (
              <StackedNavigationButton
                data-testid="identify-street-parcels-navigation"
                icon={<PictureInPictureOutlined />}
                title="Review problematic parcels"
                onClick={handleIdentifyStreetParcels}
                disabled={isLocked}
                sx={{ mt: 0, mb: 0.5 }}
                iconSize="small"
                variant="outlined"
              />
            )}

            <StackedNavigationButton
              data-testid="data-prep-settings-navigation"
              icon={<Settings />}
              title="Design preferences"
              subtitle={designPreferencesSummary}
              onClick={handleOpenSettings}
              disabled={isLocked}
              sx={{ mt: 0, mb: 0.5 }}
              iconSize="small"
              variant="outlined"
            />

            <RunDataPrep
              status={dataPrepStatus}
              canRun={canDataPrep}
              readOnly={isLocked}
              generate={generate}
              onGenerateChange={setGenerate}
              onRun={handleDataPrep}
            />
          </PanelGroup>

          <PanelGroup title="Network sources">
            <InputDataPanel readOnly={readOnly} inputs={extendedNetworkSourceLayerGroupList} dense />
          </PanelGroup>

          {hasOverlaysModule && canEditOverlays && (
            <PanelGroup title="Reference data">
              <StackedNavigationButton
                icon={<Layers />}
                title="Manage overlays"
                onClick={handleOpenOverlays}
                disabled={isLocked}
                iconSize="small"
                variant="outlined"
                sx={{ m: 0 }}
              />
            </PanelGroup>
          )}

          <PanelGroup title="Refine">
            <StackedNavigationButton
              icon={<Edit />}
              title="Modify data"
              onClick={handleModifyData}
              disabled={isLocked}
              iconSize="small"
              variant="outlined"
              sx={{ m: 0 }}
            />
          </PanelGroup>
        </>
      )}

      {stages && (isDataPrepping || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: dataPrepStageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {isDataPrepping && (
        <Button fullWidth variant="contained" size="small" sx={{ my: 1 }} disabled={!cancelWorkflow || isLocked} onClick={handleCancelDataPrep}>
          Cancel
        </Button>
      )}

      {canEditOverlays && <OverlaysSettingModal open={showOverlays} onClose={() => setShowOverlays(false)} />}
    </>
  );
};

export default DataPrepPanel;
